import { type JSX, useEffect, useState } from 'react';

import {
  type Belegart,
  type Herkunft as HerkunftAntwort,
  type Herkunftsanfrage,
  herkunft as holeHerkunft,
  tagText,
  wertText,
} from '../api/berichte';
import { ApiFehler } from '../api/fehler';
import { Dialog } from '../komponenten/Dialog';
import { Meldung } from '../komponenten/Meldung';

/**
 * Die Zelle, deren Belege gezeigt werden — ohne Beschreibung und Seite. In
 * einer Kreuztabelle (Auftrag CRM-46) dazu die Spalte: ein Wert der
 * Aufteilung oder die Summe.
 */
export type Zelle = Pick<Herkunftsanfrage, 'kennzahl' | 'zeitraum' | 'zeile' | 'spalte'>;

const ARTNAME: Readonly<Record<Belegart, string>> = {
  rechnung: 'Rechnung',
  buchung: 'Buchung',
  angebot: 'Angebot',
  termin: 'Termin',
  kunde: 'Kunde',
  honorar: 'Honorar',
  auftrag: 'Auftrag',
  gutschein: 'Gutschein',
  produkt: 'Produkt',
};

/**
 * HERKUNFT BIS ZUM BELEG (AUS-07, Auftrag #27).
 *
 * Die Einzelbelege hinter einer Zelle des Ergebnisses — Rechnungen,
 * Buchungen, Angebote, Termine, Kunden oder Honorare, je nach Kennzahl —
 * seitenweise, mit Sprung in die Akte. Die API rechnet sie mit derselben
 * Bedingung wie die Zahl, samt Filter, Vergleichszeitraum und Sichtbarkeit
 * (AUS-06); die Summe unten ist die Summe ALLER Belege, nicht nur dieser Seite,
 * und ergibt die Zahl der Zelle.
 *
 * Wohin ein Beleg springt, entscheidet die Arbeitsfläche: Termine und Honorare
 * haben keine eigene Akte; sie stehen ohne Sprung da, statt eines Knopfes, der
 * nichts tut.
 */
export function Herkunft({
  beschreibung,
  zelle,
  beiBeleg,
  beiSchliessen,
}: {
  readonly beschreibung: Herkunftsanfrage['beschreibung'];
  readonly zelle: Zelle;
  /** Sprung in die Akte; `undefined`, wenn es für diese Art keine gibt. */
  readonly beiBeleg: (art: Belegart, id: string, nummer: string | null) => (() => void) | undefined;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const [seite, setzeSeite] = useState(1);
  const [antwort, setzeAntwort] = useState<HerkunftAntwort | undefined>(undefined);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  useEffect(() => {
    let aktuell = true;
    void holeHerkunft({ beschreibung, ...zelle, seite })
      .then((h) => {
        if (aktuell) {
          setzeAntwort(h);
          setzeFehler(undefined);
        }
      })
      .catch((u: unknown) => {
        if (aktuell && u instanceof ApiFehler) setzeFehler(u);
      });
    return () => {
      aktuell = false;
    };
  }, [beschreibung, zelle, seite]);

  const k = antwort?.kennzahl;
  /* Quote und Durchschnitt (Auftrag #28): je Beleg Zaehler und Nenner. */
  const quote = k?.aggregat === 'quote' || k?.aggregat === 'durchschnitt';
  const seiten = antwort === undefined ? 1 : Math.max(1, Math.ceil(antwort.anzahl / antwort.seitengroesse));

  return (
    <Dialog
      titel={k === undefined ? 'Belege' : `Belege: ${k.name}`}
      unterzeile={
        antwort === undefined
          ? undefined
          : `${antwort.zeile} · ${tagText(antwort.zeitraum.von)} – ${tagText(antwort.zeitraum.bis)}`
      }
      breite="breit"
      beiSchliessen={beiSchliessen}
    >
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {antwort === undefined && fehler === undefined && (
        <p className="hilfe" role="status">
          Die Belege werden gelesen …
        </p>
      )}
      {antwort !== undefined && k !== undefined && (
        <>
          <p className="hinweiszeile" role="status">
            {antwort.anzahl === 0
              ? 'Hinter dieser Zahl steht kein Beleg.'
              : `${String(antwort.anzahl)} ${antwort.anzahl === 1 ? 'Beleg' : 'Belege'} · Summe ${wertText(antwort.summe.wert, k.einheit)}${
                  quote ? ` (${antwort.summe.zaehler ?? '0'} von ${antwort.summe.nenner ?? '0'})` : ''
                }`}
          </p>
          {antwort.belege.length > 0 && (
            <div className="tabellenhuelle">
              <table className="liste__tabelle herkunft">
                <caption className="nur-vorlesen">Belege hinter {k.name}, Seite {seite}</caption>
                <thead>
                  <tr>
                    <th scope="col">Beleg</th>
                    <th scope="col">Bezeichnung</th>
                    <th scope="col">{k.zeitfeld}</th>
                    {quote ? (
                      <>
                        <th scope="col" className="zahl">
                          Zähler
                        </th>
                        <th scope="col" className="zahl">
                          Nenner
                        </th>
                      </>
                    ) : (
                      <th scope="col" className="zahl">
                        {k.name}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {antwort.belege.map((b) => {
                    const sprung = beiBeleg(b.art, b.id, b.nummer);
                    const name = `${ARTNAME[b.art]}${b.nummer === null ? '' : ` ${b.nummer}`}`;
                    return (
                      <tr key={b.id}>
                        <th scope="row">
                          {sprung === undefined ? (
                            name
                          ) : (
                            <button type="button" className="knopf knopf--verweis" onClick={sprung}>
                              {name}
                            </button>
                          )}
                        </th>
                        <td>{b.bezeichnung}</td>
                        <td className="liste__einzeilig">{tagText(b.tag)}</td>
                        {quote ? (
                          <>
                            <td className="zahl">{b.zaehler}</td>
                            <td className="zahl">{b.nenner}</td>
                          </>
                        ) : (
                          <td className="zahl">{wertText(b.wert, k.einheit)}</td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {seiten > 1 && (
            <div className="knopfreihe">
              <button type="button" className="knopf knopf--klein" disabled={seite <= 1} onClick={() => setzeSeite(seite - 1)}>
                Vorige Seite
              </button>
              <span>
                Seite {seite} von {seiten}
              </span>
              <button
                type="button"
                className="knopf knopf--klein"
                disabled={seite >= seiten}
                onClick={() => setzeSeite(seite + 1)}
              >
                Nächste Seite
              </button>
            </div>
          )}
        </>
      )}
    </Dialog>
  );
}
